// SKADE Sentralbord — ett samlet samband (Element-inspirert): søk-som-du-skriver
// over rom/kanaler OG personer, samtale med historikk, ett skrivefelt som selv
// vet konteksten (kanal-chat vs. GNÅ 1:1 vs. GNÅ-rom). Erstatter de spredte
// panelene (Kontakter · Chat/TOC · GNÅ 1:1). Godkjent design:
// apps/cop/design/sentralbord-spec.html.
//
// Datakilder (ekte):
//  - Kanal-chat: ChatEvent-strøm (reduceChat), sendes via onEvent → hub-collab,
//    persisteres i hubbens ChatBoard og replayes org-scopet ved tilkobling.
//  - GNÅ 1:1 / 1:flere: utgående via POST /gna/outbound (kind dm|room);
//    innkommende 1:1 via ingestInbound (gnadm).
//  - Kontakter: orgDirectory() + lagrede/sette kontakter (fra main).
//  - Vedlegg/tale: POST /media (hub) → ChatEvent kind image/file/voice.
//  - Identitet: GET /auth/me, POST /gna/link (privat GNÅ-bruker, «swap»).
// Ren DOM, all tekst via textContent (ingen XSS).
//
// HVA SOM BLE FLYTTET HIT, OG HVORFOR (ryddingen av kommunikasjonsflaten):
// COP-en hadde NI innganger til samband, og tre av dem endte i 1:1: Feltveksler,
// «GNÅ — 1:1 / melding ut» (`ui/gna-dm.ts`) og Kontakter → samme rå skjema. To
// ulike grensesnitt for samme handling. Før de duplikate destinasjonene ble
// fjernet fra dock/kommandopalett, ble kapabilitetene som BARE fantes der flyttet
// inn hit — ellers hadde en fjerning tatt med seg funksjonalitet:
//   fra `ui/gna-dm.ts`  → 1:flere (Matrix-rom, `!rom:domene`), adressering av en
//                         id som ikke er kontakt, tilgivende id-normalisering,
//                         og identitets-lenka til privat GNÅ-bruker («swap»).
//   fra `ui/chat.ts`    → vedlegg (bilde/fil), talemelding (opptak), svar på
//                         melding (replyTo) og skrive-indikator.
import {
  reduceChat, reduceChannels, type ChatEvent, type ReducedMessage, type ChannelInfo,
  orgDirectory, mergeContacts, skadeMatrixUserId, isMatrixUserId, isMatrixRoomId,
  normalizeMatrixId, gnaUserIdFromPhone, type Contact,
} from '@skade/contracts';
import { icon } from './icons.js';
import { alderTekstKort } from './kontaktkort.js';
import {
  MAKS_POLL, erEndelig, kvitteringVisning, lesStatusSvar, nestePollMs,
  type SambandStatus,
} from './sambandstatus.js';

export type ChatSend = (e: ChatEvent) => void;

export interface SentralbordHooks {
  selfId: string;
  /** Send en kanal-chat-hendelse (hub re-stempler avsender). */
  onChat: ChatSend;
  /** Lagrede GNÅ-kontakter (persisteres av main). */
  getSaved?: () => Contact[];
  /** Auto-lærte kontakter (fra GNÅ-innboksen). */
  getSeen?: () => Contact[];
  embedded?: boolean;
  /** Åpne Feltveksler i eget vindu (egen skjerm). Gir en tydelig header-knapp
   * i stedet for den generiske flytende ⤢-pillen (som ellers dekker × / medlem). */
  onPopout?: () => void;
  /** Hjemmeserver-domenet, til tilgivende adressering (bare localpart holder). */
  defaultDomain?: string;
  /**
   * Søk i katalogen over ALLE registrerte GNÅ-brukere (hub: `/gna/brukere`).
   *
   * Uten denne kroken filtrerer søket bare LOKALE kontakter — enhetskontoene,
   * lagrede kontakter og folk man har snakket med før — og en person man aldri
   * har snakket med ga «Ingen treff». Eierkrav 2026-08-13: alle som har
   * registrert seg i GNÅ skal finnes i Kontakter.
   *
   * Injisert (ikke `fetch` her inne) fordi Feltveksleren enhetstestes i jsdom
   * uten nettverk.
   */
  sokGnaBrukere?: (q: string) => Promise<GnaKatalogTreff[]>;
  /**
   * Finn personen i kartet: fly til siste kjente posisjon.
   *
   * ⚠ RETURNERER ALDEREN, IKKE BARE «FUNNET». Et spor på ni minutter er ikke
   * «her er han» — det er «her VAR han». Kortet skriver alderen ut, fordi en
   * knapp som panorerer til et gammelt punkt uten å si det er den samme løgnen
   * som et estimat vist som et målt tall.
   *
   * `null` = ingen posisjon mottatt for denne personen. Da skal knappen si det,
   * ikke gjøre ingenting.
   */
  onFinnIKart?: (matrixId: string) => { lat: number; lon: number; tsMs: number } | null;
  /**
   * Kalles ved hver INNKOMMENDE melding operatøren ikke ser på nå.
   *
   * Feltveksleren varsler selv (lyd + teller i vindustittel + systemvarsel), for
   * at varslingen skal være DEN SAMME docked og i eget vindu. Denne kroken er
   * for det hovedvinduet i tillegg kan gjøre — toast over kartet, merke i
   * verktøylinja — og er valgfri nettopp derfor.
   */
  onVarsel?: (v: Varsel) => void;
}

/** En innkommende melding operatøren ikke har sett. */
export interface Varsel {
  /** Samtale-id (`ch:net`, `dm:@gna…`) — så mottakeren kan åpne rett samtale. */
  convId: string;
  /** Avsender, kort form (uten sigil/domene). */
  fra: string;
  /** Meldingsteksten (kan være tom for vedlegg). */
  tekst: string;
  /** Antall uleste innkommende i denne økta etter denne meldingen. */
  uleste: number;
}

/** Én GNÅ-bruker fra hub-katalogen (speiler `GnaBruker` i hub-en). */
export interface GnaKatalogTreff {
  matrixId: string;
  navn: string;
  nummer: string;
}

interface DmMsg {
  id: string; from: string; text: string; tsMs: number; mine: boolean;
  pending?: boolean; failed?: boolean;
  /** Leveringsstatus fra hubben (`/samband/status`). Se `sambandstatus.ts`. */
  status?: SambandStatus;
  /** Grunnen når leveringen feilet — ordrett fra broen. */
  grunn?: string;
  /** Antall leveringsforsøk hubben har gjort. */
  forsok?: number;
  /** Da hubben la meldingen i kø. */
  koetMs?: number;
}
interface Conv {
  id: string;
  /** 'channel' = hub-kanal (SKADE-native) · 'dm' = GNÅ 1:1 · 'gnarom' = GNÅ-rom (1:flere). */
  kind: 'channel' | 'dm' | 'gnarom';
  name: string;
  sub: string;
  channel?: string;     // for kind='channel'
  matrixId?: string;    // for kind='dm' | 'gnarom'
  contact?: Contact;    // for kind='dm' (kort)
}

/** En adresse operatøren har skrevet inn: GNÅ-bruker (1:1) eller GNÅ-rom (1:flere).
 * `fraNummer` er satt når id-en ble UTLEDET av et telefonnummer i søkefeltet. */
export interface Adresse { kind: 'dm' | 'rom'; id: string; fraNummer?: string }

/**
 * Tolk fritekst i søkefeltet som en Matrix-ADRESSE, ikke et navnesøk.
 *
 * HVORFOR DENNE FINNES: dette var den ENE tingen det gamle råskjemaet («GNÅ —
 * 1:1 / melding ut») kunne som Feltveksleren ikke kunne — sende til noen som
 * ikke står i katalogen, og til et Matrix-ROM (1:flere). Der måtte adressen
 * limes inn i et eget felt; her er søkefeltet inngangen, så det er ÉN vei inn
 * til en samtale uansett om mottakeren er kjent eller ikke.
 *
 * Sigilet (@ eller !) er det som skiller adresse fra navnesøk — «dan» skal søke
 * i katalogen, «@dan» skal kunne adresseres. Domenet fylles ut om det mangler
 * (samme tilgivende regel som Kontakter bruker: `normalizeMatrixId`).
 *
 * OG: et TELEFONNUMMER er også en adresse. GNÅ-id-er er utledet deterministisk
 * av nummeret («gna» + E.164 uten «+», samme regel som GNÅ-appen og
 * OTP-tjenesten), så «91994407» kan alltid oversettes til
 * @gna4791994407:<domene> — uten katalogtjeneste. Uten denne regelen ga et
 * nummer i søkefeltet bare «Ingen treff», og operatøren hadde ingen vei fram
 * til en GNÅ-bruker som ennå ikke sto i kontaktlista.
 */
export function tolkAdresse(raw: string, defaultDomain: string): Adresse | null {
  const v = (raw ?? '').trim();
  if (v.length < 2) return null;
  if (!/^[@!]/.test(v)) {
    const gna = gnaUserIdFromPhone(v, defaultDomain);
    return gna ? { kind: 'dm', id: gna, fraNummer: v } : null;
  }
  const rom = v.startsWith('!');
  const id = normalizeMatrixId(v, defaultDomain, rom ? '!' : '@');
  if (rom) return isMatrixRoomId(id) ? { kind: 'rom', id } : null;
  return isMatrixUserId(id) ? { kind: 'dm', id } : null;
}

/**
 * Skal en innkommende melding varsles?
 *
 * Ren regel, skilt ut fra panelet fordi den er DET som avgjør om samband roper
 * eller er stumt, og fordi denne appens testoppsett ikke har jsdom (panelet selv
 * kan ikke instansieres i test). Kravet i klartekst: varsle med mindre
 * operatøren faktisk ser meldingen komme inn — altså panelet åpent, PÅ den
 * samtalen, i et vindu som har fokus.
 */
export function skalVarsle(v: {
  /** Er panelet åpent? (Embedded/pop-out er alltid åpent.) */
  apen: boolean;
  /** Samtalen operatøren står i. */
  aktivId: string;
  /** Samtalen meldingen kom i. */
  convId: string;
  /** Har vinduet tastaturfokus? */
  harFokus: boolean;
}): boolean {
  if (!v.apen) return true;
  if (v.aktivId !== v.convId) return true;
  return !v.harFokus;
}

/** Vindustittel med uleste-teller. 0 gir grunntittelen urørt. */
export function varseltittel(uleste: number, grunntittel: string): string {
  return uleste > 0 ? `(${uleste}) ${grunntittel}` : grunntittel;
}

/**
 * Er dette en SKADE-konto (stilling/rolle) og ikke en GNÅ-brukers telefon?
 *
 * `skade_`-prefikset er provisjoneringsregelen for rolle- og stillingskontoer
 * (`stillingLocalpart` i @skade/contracts), så den er sann på tvers av
 * distrikter. Ren funksjon → testbar.
 */
export function erSkadeKonto(matrixId: string): boolean {
  const lp = (matrixId ?? '').replace(/^@/, '').split(':')[0] ?? '';
  return lp.startsWith('skade_') || lp === 'skade-bro';
}

const NET = 'net';
const READ_KEY = 'skade.sb.read';         // convId → sist-lest tsMs (uleste-merker)
// Kvitteringsreaksjoner (comms-primitiver): rask ROGER/WILCO uten å skrive svar.
const ACK_EMOJI = ['👍', '✅', '🅁', '📍', '❗'];
// Pop-out-ikon (ramme + pil ut) til «Eget vindu»-knappen i hodet.
const POPOUT_SVG =
  '<svg viewBox="0 0 20 20" width="14" height="14" stroke="currentColor" fill="none" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M11 3h6v6"/><path d="M17 3l-7 7"/><path d="M15 11.5V16a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 16V6.5A1.5 1.5 0 0 1 4.5 5H9"/></svg>';
// Medlem-ikon (to personer) til rom-info-knappen i kanalhodet.
const MEMBERS_SVG =
  '<svg viewBox="0 0 20 20" width="15" height="15" stroke="currentColor" fill="none" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<circle cx="7.5" cy="7" r="2.4"/><path d="M3 15a4.5 4.5 0 0 1 9 0"/><path d="M13.5 5.4a2.4 2.4 0 0 1 0 4.5M14.5 15a4.5 4.5 0 0 0-2-3.7"/></svg>';
// Tomtilstand-glyfer (dempet linje-ikon per kontekst).
const EMPTY_SVG: Record<string, string> = {
  search: '<circle cx="9" cy="9" r="6"/><path d="M20 20l-6-6"/>',
  chat: '<path d="M4 5h16v10H8l-4 4z"/>',
  members: '<circle cx="9" cy="8" r="3"/><path d="M3 19a6 6 0 0 1 12 0"/><path d="M17 7a3 3 0 0 1 0 6"/>',
};
// Skrive-indikator: hvor lenge en «X skriver …» står før den regnes som utløpt,
// og hvor sjelden vi sender egen (strupt, ellers blir det ett event per tast).
const TYPING_TTL_MS = 5000;
const TYPING_STRUPE_MS = 2500;
const now = () => Date.now();
const short = (id: string) => id.replace(/^[@!#]/, '').replace(/:.*$/, '');

const hhmm = (ms: number) => new Date(ms).toISOString().slice(11, 16) + 'Z';
const rid = () => `c-${now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;

export class Sentralbord {
  readonly el: HTMLElement;
  private open = false;
  private log: ChatEvent[] = [];                    // kanal-chat (alle kanaler)
  private dm = new Map<string, DmMsg[]>();          // matrixId → 1:1-historikk
  private activeId = 'ch:net';
  private q = '';
  private matrixId: string;
  private saved: Contact[] = [];
  /** Mottakere adressert utenfra (Kontakter/palett) som ikke finnes i katalog
   *  eller DM-historikk ennå. Se `apneDm`. */
  private adressert = new Map<string, Contact>();
  private lastRead = new Map<string, number>();     // convId → sist-lest tsMs
  private msgQuery = '';                             // søk i aktiv samtale (find-in-room)
  /** Treff fra GNÅ-katalogen (hub) for gjeldende søk. */
  private gnaTreff: GnaKatalogTreff[] = [];
  private katalogTimer: number | null = null;
  private findOpen = false;
  private unreadBoundary = 0;                        // tsMs for «Nye meldinger»-skille i aktiv samtale
  private replyTo: string | null = null;             // svar-mål i aktiv kanal (ChatEvent.replyTo)
  private lastTypingSent = 0;                        // strupe for egen skrive-indikator
  private recorder: MediaRecorder | null = null;     // pågående taleopptak
  private recStartMs = 0;
  private linked: string | null = null;              // lenket privat GNÅ-bruker («swap»)
  // ── Varsling (lyd + tittelteller + systemvarsel) ─────────────────────────
  /** Uleste innkommende i denne økta — vises i vindustittelen. */
  private ulesteTotalt = 0;
  /** Vindustittelen slik den var før vi begynte å telle i den. */
  private grunntittel = '';
  private lydKontekst: AudioContext | null = null;
  /** Har vi alt spurt om lov til systemvarsler? (Spørres én gang, på klikk.) */
  private varselLovSpurt = false;
  // DOM-referanser
  private listEl!: HTMLElement;
  private headEl!: HTMLElement;
  private msgsEl!: HTMLElement;
  private findEl!: HTMLElement;
  private typingEl!: HTMLElement;
  private replyEl!: HTMLElement;
  private vedleggNoteEl!: HTMLElement;
  private targetEl!: HTMLElement;
  private ta!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private attachBtn!: HTMLButtonElement;
  private micBtn!: HTMLButtonElement;
  private fileInput!: HTMLInputElement;
  private searchEl!: HTMLInputElement;

  constructor(private hooks: SentralbordHooks) {
    this.matrixId = skadeMatrixUserId(hooks.selfId);
    try { this.lastRead = new Map(Object.entries(JSON.parse(localStorage.getItem(READ_KEY) ?? '{}'))); } catch { /* ok */ }
    this.el = this.build();
    if (hooks.embedded) { this.open = true; this.el.classList.add('open', 'embedded'); }
    // Første berøring i panelet er den brukerhandlingen nettleseren krever før
    // lyd får spille. I embedded/pop-out-modus finnes ingen `toggle`-knapp å
    // hente den fra, så uten dette ville pop-out-vinduet vært stumt.
    try { this.el.addEventListener('pointerdown', () => this.vekkLyd(), { once: true }); } catch { /* ok */ }
    // Får vinduet fokus, har operatøren sett det: telleren i tittelen skal bort.
    try {
      (globalThis as { addEventListener?: typeof addEventListener }).addEventListener?.(
        'focus', () => { if (this.open) this.nullstillVarsler(); },
      );
    } catch { /* ok */ }
    this.markRead(this.activeId);
    this.renderAll();
    // Skrive-indikatorer utløper av seg selv (ingen «stopp»-event finnes).
    setInterval(() => this.renderTyping(), 2000);
    // Ekte identitet + evt. lenket privat GNÅ-bruker fra hubben. Feiler stille i
    // demo/offline: da står den utledede @skade_-id-en, som er sann nok.
    void this.refreshMe();
  }

  /** Hjemmeserver-domenet — fra egen Matrix-id, med hook-verdien som reserve. */
  private domene(): string {
    return this.matrixId.split(':').slice(1).join(':') || this.hooks.defaultDomain || 'matrix.follodefence.com';
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) {
      this.vekkLyd();          // brukerhandling → lyd og varsel-lov kan settes opp
      this.nullstillVarsler();
      this.searchEl.focus();
      this.renderAll();
    }
  }

  // ── Varsling ──────────────────────────────────────────────────────────────
  //
  // EIERFUNN 2026-08-13: «Hvorfor får man ingen varsling i feltveksleren når man
  // sender melding — det er jo helt på tryne». Riktig: en innkommende melding
  // satte bare en stille prikk i samtalelista. Ser du på kartet, eller har
  // Feltveksleren i et vindu bak, gikk meldingen upåaktet hen. Et sambandsmiddel
  // som ikke roper er ikke et sambandsmiddel.
  //
  // Varslingen ligger HER, i panelet, og ikke i hovedvinduets skall — nettopp
  // fordi eieren krevde at pop-out skal være LIK: legger vi den i skallet, får
  // det poppede vinduet ingenting.
  //
  // Tre kanaler, i økende påtrengenhet:
  //   1. Teller i vindustittelen — synlig i fanerad/Dock uten at vinduet er
  //      fremst. Virker alltid, krever ingen tillatelse.
  //   2. Lyd — kort pip, generert i WebAudio (ingen lydfil å laste, virker
  //      offline). Nettleseren krever en brukerhandling først; derfor vekkes
  //      lydkonteksten i `toggle`/`openConversation`.
  //   3. Systemvarsel — bare hvis operatøren har gitt lov. Vi spør ÉN gang, på
  //      et klikk, og aldri på nytt om svaret var nei.

  /** Er meldingen usett akkurat nå? (Panelet lukket, annen samtale, eller
   * vinduet i bakgrunnen.) Å pipe for en melding operatøren ser komme inn i
   * ruta foran seg er bare støy. */
  private erUsett(convId: string): boolean {
    let harFokus = true; // uten fokus-API antar vi at vinduet er sett
    try { if (typeof document.hasFocus === 'function') harFokus = document.hasFocus(); } catch { /* ok */ }
    return skalVarsle({ apen: this.open, aktivId: this.activeId, convId, harFokus });
  }

  /** Varsle om én innkommende melding. Alt her er «best effort»: en blokkert
   * lydkontekst eller et avslått systemvarsel skal ikke hindre de andre. */
  private varsle(convId: string, fra: string, tekst: string) {
    this.ulesteTotalt += 1;
    this.oppdaterTittel();
    this.pip();
    this.systemvarsel(fra, tekst);
    try {
      this.hooks.onVarsel?.({ convId, fra: short(fra), tekst, uleste: this.ulesteTotalt });
    } catch { /* en feil hos mottakeren skal ikke velte inntaket */ }
  }

  private oppdaterTittel() {
    try {
      if (!this.grunntittel) this.grunntittel = document.title || 'SKADE';
      document.title = varseltittel(this.ulesteTotalt, this.grunntittel);
    } catch { /* ingen document.title */ }
  }

  /** Nullstill telleren — operatøren har sett meldingene. */
  private nullstillVarsler() {
    if (this.ulesteTotalt === 0) return;
    this.ulesteTotalt = 0;
    this.oppdaterTittel();
  }

  /** Kort pip (to toner, avtagende) — hørbart uten å være en alarm. */
  private pip() {
    try {
      const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (!Ctx) return;
      this.lydKontekst ??= new Ctx();
      const ctx = this.lydKontekst;
      void ctx.resume?.();
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, t0);
      osc.frequency.setValueAtTime(660, t0 + 0.09);
      // Rampe ned til (nesten) null: en brå stopp gir et smell i høyttaleren.
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.14, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.24);
    } catch { /* lyd blokkert / ikke støttet */ }
  }

  /** Vekk lydkonteksten på en ekte brukerhandling. Uten dette står den
   * «suspended» til første klikk, og det FØRSTE varselet blir stumt. */
  private vekkLyd() {
    try {
      const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (!Ctx) return;
      this.lydKontekst ??= new Ctx();
      void this.lydKontekst.resume?.();
    } catch { /* ok */ }
    this.sporOmVarsellov();
  }

  /** Spør om lov til systemvarsler — én gang, og bare i en klikk-kontekst. */
  private sporOmVarsellov() {
    if (this.varselLovSpurt) return;
    this.varselLovSpurt = true;
    try {
      const N = (globalThis as { Notification?: typeof Notification }).Notification;
      if (N && N.permission === 'default') void N.requestPermission();
    } catch { /* ikke støttet */ }
  }

  private systemvarsel(fra: string, tekst: string) {
    try {
      const N = (globalThis as { Notification?: typeof Notification }).Notification;
      if (!N || N.permission !== 'granted') return;
      // `tag` per avsender: fem meldinger fra samme person skal erstatte
      // hverandre, ikke stable fem bobler oppå hverandre.
      new N(`SKADE · ${short(fra)}`, {
        body: tekst.slice(0, 160) || 'Ny melding',
        tag: `skade-sb-${short(fra)}`,
      });
    } catch { /* systemvarsel blokkert */ }
  }

  // ── Inntak ────────────────────────────────────────────────────────────────
  /** Kanal-chat-hendelse (fra hub-fanout/replay). */
  ingestChat(e: ChatEvent) {
    this.log.push(e);
    if (this.log.length > 6000) this.log.splice(0, this.log.length - 6000);
    // Varsle FØR gjentegning: bare ekte meldinger fra andre. `typing`, `react`,
    // `member`, `topic`, `edit` og `delete` er ikke noe å pipe for, og egne
    // meldinger enda mindre.
    if (e.op === 'post' && e.author !== this.hooks.selfId) {
      const convId = `ch:${e.channel || NET}`;
      if (this.erUsett(convId)) this.varsle(convId, e.author, e.text ?? '');
    }
    const a = this.active();
    if (a.kind === 'channel') {
      // Kanal-metadata (medlem/tema) endrer hodet; meldinger endrer tråden.
      // Skrive-indikator rører BARE sin egen linje: bygde vi tråden på nytt for
      // hvert tastetrykk hos motparten, ville lista rulle til bunn under
      // fingrene på den som leser lenger opp.
      if (e.op === 'typing') this.renderTyping();
      else if (e.op === 'member' || e.op === 'topic') this.renderHead();
      else this.renderMsgs();
      if (this.open && (e as { channel?: string }).channel === a.channel) this.markRead(a.id);
    }
    this.renderList();
  }
  /** Innkommende GNÅ 1:1-svar (gnadm). */
  ingestInbound(from: string, text: string, tsMs: number) {
    const arr = this.dm.get(from) ?? [];
    arr.push({ id: rid(), from, text, tsMs, mine: false });
    this.dm.set(from, arr);
    if (this.erUsett(`dm:${from}`)) this.varsle(`dm:${from}`, from, text);
    // sørg for at samtalen finnes i lista (auto-lært)
    if (this.active().id === `dm:${from}`) { this.renderMsgs(); if (this.open) this.markRead(`dm:${from}`); }
    this.renderList();
  }

  private setSaved() { this.saved = mergeContacts(this.hooks.getSaved?.() ?? [], this.hooks.getSeen?.() ?? [], []); }

  // ── Samtaler ────────────────────────────────────────────────────────────
  /** Alle samtaler: nett + kanaler sett i chat-loggen + personer (katalog + DM-er). */
  /**
   * Oppslåtte navn fra GNÅ-katalogen, per Matrix-id.
   *
   * ⚠ HVORFOR DENNE MÅTTE TIL: `convs()` satte `name: short(from)` for alle vi
   * bare kjenner fra DM-historikk — altså den rå id-en («gna4796000526») brukt
   * SOM OM den var et navn. Katalog-kroken fantes, men ble bare brukt til
   * søk-som-du-skriver; ingenting etterfylte navnet på en samtale som alt var
   * åpnet. Operatøren så derfor et telefonnummer der det skulle stått «Ivar», og
   * kunne ikke finne ham ved å søke på navnet (eierrapport 2026-08-18).
   *
   * `null` = slått opp, men katalogen kjenner ingen. Da skal vi IKKE spørre på
   * nytt i det uendelige — og vi skal si «navn ikke registrert», ikke vise
   * id-en som om den var et navn.
   */
  private navnCache = new Map<string, { navn: string; nummer: string } | null>();
  /** Id-er det pågår oppslag for, så samme id ikke spørres to ganger. */
  private navnPagar = new Set<string>();

  /**
   * Slå opp navn for Matrix-id-er vi ikke har navn på.
   *
   * Bruker katalog-kroken (`/gna/brukere?q=`), som matcher på `matrixId` også —
   * derfor holder det å søke på id-en og plukke det eksakte treffet. Ingen ny
   * hub-rute trengs.
   */
  private async loesNavn(ider: readonly string[]): Promise<void> {
    const hook = this.hooks.sokGnaBrukere;
    if (!hook) return;
    const mangler = ider.filter(
      (id) => !this.navnCache.has(id) && !this.navnPagar.has(id) && !isMatrixRoomId(id),
    );
    if (!mangler.length) return;
    for (const id of mangler) this.navnPagar.add(id);
    let endret = false;
    for (const id of mangler) {
      try {
        const treff = await hook(short(id));
        const eksakt = treff.find((t) => t.matrixId === id);
        this.navnCache.set(id, eksakt ? { navn: eksakt.navn, nummer: eksakt.nummer } : null);
        if (eksakt) endret = true;
      } catch {
        // ⚠ IKKE CACHE EN FEIL SOM «INGEN NAVN». En Synapse-feil er midlertidig;
        // et manglende navn er permanent til noen registrerer det. Blandes de,
        // står operatøren igjen med et telefonnummer for alltid etter ett blaff.
        this.navnPagar.delete(id);
      } finally {
        this.navnPagar.delete(id);
      }
    }
    if (endret) this.renderAll();
  }

  /** Navnet vi skal VISE for en Matrix-id, og om det er et ekte navn. */
  private visningsnavn(id: string, fallback: string): { navn: string; ekte: boolean } {
    const c = this.navnCache.get(id);
    if (c?.navn) return { navn: c.navn, ekte: true };
    return { navn: fallback, ekte: false };
  }

  private convs(): Conv[] {
    this.setSaved();
    const out: Conv[] = [{ id: 'ch:net', kind: 'channel', name: 'TOC-nett', sub: 'Alle operatører · kringkasting', channel: NET }];
    // Kanaler oppdaget i innkommende feltmeldinger (bro setter channel = tropp/lag/distrikt).
    const seen = new Set<string>();
    for (const e of this.log) {
      const ch = (e as { channel?: string }).channel;
      if (ch && ch !== NET && !seen.has(ch)) {
        seen.add(ch);
        out.push({ id: `ch:${ch}`, kind: 'channel', name: ch, sub: 'Enhetskanal (GNÅ-felt)', channel: ch });
      }
    }
    // Personer: enheter (orgDirectory) + lagrede/sette + de vi har DM-historikk med.
    const units: Contact[] = orgDirectory().map((u) => ({ id: u.matrixUserId, name: u.label, sub: u.kind === 'distrikt' ? 'Distriktsstab' : `Kompani · ${u.district}`, source: 'unit' as const }));
    const people = mergeContacts(this.saved, [], units);
    const byId = new Map(people.map((p) => [p.id, p] as const));
    for (const from of this.dm.keys()) {
      if (byId.has(from)) continue;
      // Navnet hentes fra katalogen når den kjenner personen; ellers står id-en,
      // men da MERKET som «navn ikke registrert» framfor å utgi seg for et navn.
      const n = this.visningsnavn(from, short(from));
      byId.set(from, {
        id: from, name: n.navn,
        sub: n.ekte ? (this.navnCache.get(from)?.nummer || 'GNÅ-bruker') : 'GNÅ-bruker · navn ikke registrert',
        source: 'seen',
      });
    }
    // Adressert utenfra (`apneDm`) uten å finnes i katalog eller historikk ennå.
    for (const [id, c] of this.adressert) if (!byId.has(id)) byId.set(id, c);
    // Etterfyll navn i bakgrunnen for de vi mangler. Ett oppslag per id, og
    // `renderAll()` kalles bare når noe FAKTISK ble funnet — ellers ville en
    // liste uten navn gitt en evig render-løkke.
    void this.loesNavn([...byId.keys()]);
    for (const p of byId.values()) {
      // Et Matrix-ROM er ikke en person: det hører under «Rom / kanaler» og
      // sendes som kind='room' (1:flere) — se `sendUt`.
      const rom = isMatrixRoomId(p.id);
      out.push({
        id: `${rom ? 'rom' : 'dm'}:${p.id}`, kind: rom ? 'gnarom' : 'dm',
        name: p.name, sub: p.sub || short(p.id), matrixId: p.id, contact: rom ? undefined : p,
      });
    }
    return out;
  }

  /**
   * Åpne (og om nødvendig opprette) en 1:1-samtale mot en Matrix-id.
   *
   * HVORFOR DETTE ER OFFENTLIG: Kontakter-panelet og kommandopaletten pekte før
   * på et EGET råteknisk skjema («GNÅ — 1:1 / melding ut») der operatøren måtte
   * lime inn full adresse selv. Beviset på at det var galt lå i Kontakter: den
   * hadde en «Kopier»-knapp hvis eneste jobb var å flytte adressen over til det
   * andre skjemaet. To funksjoner som ikke snakket sammen, med en knapp bygget
   * for å kompensere. Nå er det ÉN samtale-inngang, og Kontakter er en søkbar
   * liste INN i den.
   *
   * `adressert` er nødvendig fordi `convs()` bare kjenner katalogen, lagrede
   * kontakter og de vi har historikk med. Uten den ville en helt ny mottaker
   * falle tilbake til TOC-nett — altså kringkasting der operatøren ba om 1:1.
   *
   * Tar også en ROM-id (`!rom:domene`) → 1:flere-samtale, siden det var det
   * gamle skjemaets andre halvdel («1:flere — rom»).
   */
  apneDm(matrixUserId: string, navn?: string) {
    const raw = (matrixUserId ?? '').trim();
    const rom = raw.startsWith('!');
    const id = rom ? normalizeMatrixId(raw, this.domene(), '!')
      : isMatrixUserId(raw) ? raw
        : raw.startsWith('@') ? normalizeMatrixId(raw, this.domene(), '@')
          : skadeMatrixUserId(raw);
    if (!this.adressert.has(id) && !this.dm.has(id)) {
      this.adressert.set(id, {
        id, name: navn?.trim() || short(id),
        sub: rom ? 'GNÅ-rom · 1:flere' : 'GNÅ-bruker', source: 'seen',
      });
    }
    if (!this.open) this.toggle();
    this.openConversation(`${rom ? 'rom' : 'dm'}:${id}`);
  }
  /** Åpne 1:1 OG send en melding programmatisk — brukt av ORBAT-creatoren til å
   * levere enrolment-nøkler («send key») via samme vei som operatørens egen
   * sending (`sendUt`), så meldingen får historikk, pending/failed-status og
   * synlig kvittering i samtalen. */
  sendDm(matrixUserId: string, navn: string | undefined, text: string) {
    this.apneDm(matrixUserId, navn);
    const c = this.active();
    if (c && c.kind !== 'channel' && c.matrixId) void this.sendUt(c.matrixId, text);
  }

  private active(): Conv { return this.convs().find((c) => c.id === this.activeId) ?? this.convs()[0]; }

  // ── Kanal-metadata + uleste ─────────────────────────────────────────────────
  /** Gjeldende medlemskap + tema per kanal (redusert fra loggen). */
  private channels(): Map<string, ChannelInfo> { return reduceChannels(this.log); }
  private channelInfo(ch: string): ChannelInfo { return this.channels().get(ch) ?? { channel: ch, members: [] }; }

  /** Tidspunkt for siste melding i en samtale (0 om tom). */
  private lastMsgTs(c: Conv): number {
    if (c.kind === 'channel') {
      let t = 0;
      for (const e of this.log) if (e.op === 'post' && (e as { channel?: string }).channel === c.channel) t = Math.max(t, e.tsMs);
      return t;
    }
    const arr = this.dm.get(c.matrixId!) ?? [];
    return arr.length ? arr[arr.length - 1].tsMs : 0;
  }
  /** Uleste for en samtale = siste innkommende melding nyere enn sist-lest. */
  private isUnread(c: Conv): boolean {
    if (c.id === this.activeId && this.open) return false;
    const last = this.lastMsgTs(c);
    return last > 0 && last > (this.lastRead.get(c.id) ?? 0);
  }
  /** Åpne en samtale: fang uleste-grensen FØR vi merker som lest (til «Nye
   * meldinger»-skillet), nullstill søk, render. */
  private openConversation(convId: string) {
    this.unreadBoundary = this.lastRead.get(convId) ?? 0;
    this.activeId = convId;
    this.msgQuery = ''; this.findOpen = false;
    this.replyTo = null;   // svar-mål hører til ÉN samtale, ikke til panelet
    // Å åpne en samtale ER en brukerhandling: her har vi lov til å vekke
    // lydkonteksten og spørre om varsel-tillatelse, og telleren er sett.
    this.vekkLyd();
    this.nullstillVarsler();
    this.markRead(convId);
    this.renderList(); this.renderHead(); this.renderMsgs(); this.ta.focus();
  }
  private markRead(convId: string) {
    const c = this.convs().find((x) => x.id === convId);
    const last = c ? this.lastMsgTs(c) : 0;
    this.lastRead.set(convId, Math.max(last, now()));
    try { localStorage.setItem(READ_KEY, JSON.stringify(Object.fromEntries(this.lastRead))); } catch { /* full */ }
  }
  /** Er jeg (eller enheten) selv medlem/oppført på kanalen? */
  private amMember(ch: string): boolean {
    return this.channelInfo(ch).members.some((m) => m.id === this.hooks.selfId || m.id === this.matrixId);
  }

  // ── Bygg skall ────────────────────────────────────────────────────────────
  private build(): HTMLElement {
    const root = el('div', 'sb-panel');
    // Venstre kolonne
    const rail = el('aside', 'sb-rail');
    const rhead = el('div', 'sb-railhead');
    const title = el('div', 'sb-railtitle');
    title.append(el('span', 'sb-dot'), textSpan('b', '', 'Feltveksler'));
    const newRoom = btn('sb-newbtn', '＋ Nytt rom', () => this.newRoom());
    title.append(newRoom);
    const search = searchBox('Søk person, lag, tropp, kanal…', (v) => {
      this.q = v;
      this.renderList();
      this.sokKatalog(v);
    });
    this.searchEl = search.input;
    rhead.append(title, search.wrap);
    this.listEl = el('div', 'sb-list');
    rail.append(rhead, this.listEl);
    // Samtale
    const conv = el('section', 'sb-conv');
    this.headEl = el('div', 'sb-convhead');
    this.findEl = el('div', 'sb-findbar');
    this.msgsEl = el('div', 'sb-msgs');
    this.typingEl = el('div', 'sb-typing');
    const compose = el('div', 'sb-compose');
    this.replyEl = el('div', 'sb-replybar');
    this.vedleggNoteEl = el('div', 'checklist-note sb-vedleggnote');
    this.targetEl = el('div', 'sb-target');
    const cbar = el('div', 'sb-cbar');
    // Vedlegg + tale: flyttet inn fra Chat/TOC-panelet. Skjult filvelger som der.
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file'; this.fileInput.accept = 'image/*,application/pdf'; this.fileInput.style.display = 'none';
    this.fileInput.onchange = () => void this.attachFile();
    this.attachBtn = btn('sb-icbtn', '', () => this.fileInput.click());
    this.attachBtn.innerHTML = icon('attach');
    this.micBtn = btn('sb-icbtn', '', () => void this.toggleRecord());
    this.micBtn.innerHTML = icon('mic');
    this.ta = document.createElement('textarea'); this.ta.className = 'sb-ta'; this.ta.rows = 1; this.ta.placeholder = 'Skriv melding…';
    this.ta.onkeydown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); } };
    this.sendBtn = btn('sb-send', 'Send', () => this.send());
    this.sendBtn.disabled = true;
    this.ta.oninput = () => {
      this.ta.style.height = 'auto'; this.ta.style.height = Math.min(this.ta.scrollHeight, 120) + 'px';
      this.sendBtn.disabled = !this.ta.value.trim();
      this.sendTyping();
    };
    cbar.append(this.attachBtn, this.micBtn, this.fileInput, this.ta, this.sendBtn);
    compose.append(this.replyEl, this.vedleggNoteEl, this.targetEl, cbar);
    conv.append(this.headEl, this.findEl, this.msgsEl, this.typingEl, compose);
    if (!this.hooks.embedded) {
      const x = btn('sb-x', '×', () => this.toggle());
      this.headEl.append(x);
    }
    root.append(rail, conv);
    return root;
  }

  // ── Render ──────────────────────────────────────────────────────────────
  private renderAll() { this.renderList(); this.renderHead(); this.renderMsgs(); this.renderTyping(); }

  private renderList() {
    const q = this.q.trim().toLowerCase();
    const all = this.convs();
    this.listEl.innerHTML = '';
    // Adresse skrevet rett i søkefeltet (@bruker / !rom) som ikke finnes ennå:
    // tilby å åpne samtalen. Dette er inngangen som erstatter det gamle
    // «lim inn mottaker»-feltet — uten den ville en ukjent adresse bare gitt
    // «ingen treff», og operatøren måtte hatt et annet panel for å nå fram.
    const adr = tolkAdresse(this.q, this.domene());
    if (adr && !all.some((c) => c.matrixId === adr.id)) {
      this.listEl.append(textSpan('div', 'sb-grp', 'Skriv til adresse'));
      const row = el('div', 'sb-row sb-adr');
      row.tabIndex = 0;
      const av = el('div', 'sb-av ' + (adr.kind === 'rom' ? 'unit' : 'person'));
      av.textContent = adr.kind === 'rom' ? '#' : '@';
      row.append(av);
      const main = el('div', 'sb-rowmain');
      main.append(textSpan('div', 'sb-rowname', short(adr.id)));
      main.append(textSpan('div', 'sb-rowprev', adr.kind === 'rom'
        ? `Nytt GNÅ-rom (1:flere) · ${adr.id}`
        : adr.fraNummer
          ? `GNÅ-bruker utledet fra ${adr.fraNummer} · ${adr.id}`
          : `Ny 1:1 med GNÅ-bruker · ${adr.id}`));
      row.append(main);
      const apne = () => { this.q = ''; this.searchEl.value = ''; this.apneDm(adr.id); };
      row.onclick = apne;
      row.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); apne(); } };
      this.listEl.append(row);
    }
    for (const [label, kinds] of [['Rom / kanaler', ['channel', 'gnarom']], ['Personer', ['dm']]] as const) {
      const items = all.filter((c) => (kinds as readonly string[]).includes(c.kind)).filter((c) => !q || (c.name + ' ' + c.sub).toLowerCase().includes(q));
      if (!items.length) continue;
      this.listEl.append(textSpan('div', 'sb-grp', label));
      for (const c of items) {
        const unread = this.isUnread(c);
        const row = el('div', 'sb-row' + (c.id === this.activeId ? ' active' : '') + (unread ? ' unread' : ''));
        row.tabIndex = 0;
        row.append(this.avatar(c));
        const main = el('div', 'sb-rowmain');
        const top = el('div', 'sb-rowtop');
        top.append(textSpan('span', 'sb-rowname', c.name));
        if (c.kind === 'channel') {
          const n = this.channelInfo(c.channel!).members.length;
          if (n) top.append(textSpan('span', 'sb-rowcount', `· ${n}`));
        }
        if (unread) top.append(textSpan('span', 'sb-badge', '●'));
        main.append(top);
        const subrow = el('div', 'sb-rowsub');
        // MERKET MÅ SI SANNHETEN OM MOTPARTEN.
        //
        // ⚠ HVA SOM VAR GALT: alle 1:1-rader fikk merket «GNÅ» — også
        // SKADE-stillingskontoene fra enhetskatalogen (`@skade_hv-01` osv.).
        // Eieren søkte etter en person og fikk to rader merket GNÅ som IKKE var
        // GNÅ-brukere i det hele tatt, og konkluderte rimeligvis at søket var
        // ødelagt. Et merke som lyver om hvem du snakker med er verre enn intet
        // merke: i felt avgjør det om du tror du når en soldats telefon eller en
        // stab.
        const tagg: [string, string] = c.kind === 'gnarom'
          ? ['gna', 'GNÅ-ROM']
          : c.kind === 'channel'
            ? ['rom', 'KANAL']
            : erSkadeKonto(c.matrixId ?? '')
              ? ['skade', 'SKADE']
              : ['gna', 'GNÅ'];
        subrow.append(textSpan('span', 'sb-tag ' + tagg[0], tagg[1]));
        subrow.append(textSpan('span', 'sb-rowprev', this.preview(c)));
        main.append(subrow);
        row.append(main);
        const openConv = () => { this.openConversation(c.id); };
        row.onclick = openConv;
        row.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openConv(); } };
        this.listEl.append(row);
      }
    }
    // ── Katalogen: alle registrerte GNÅ-brukere ──────────────────────────
    // Sist i lista med vilje: kjente samtaler og kontakter skal rangere over
    // et katalogoppslag. Folk vi alt viser over filtreres ut, så ingen dukker
    // opp to ganger.
    const alleredeVist = new Set(all.map((c) => c.matrixId ?? c.id));
    const katalog = this.gnaTreff.filter((t) => !alleredeVist.has(t.matrixId));
    if (katalog.length) {
      this.listEl.append(textSpan('div', 'sb-grp', 'GNÅ-brukere'));
      for (const t of katalog) {
        const row = el('div', 'sb-row');
        row.tabIndex = 0;
        const av = el('div', 'sb-av person');
        av.textContent = (t.navn.trim()[0] ?? '@').toUpperCase();
        row.append(av);
        const main = el('div', 'sb-rowmain');
        main.append(textSpan('div', 'sb-rowname', t.navn));
        const subrow = el('div', 'sb-rowsub');
        subrow.append(textSpan('span', 'sb-tag gna', 'GNÅ'));
        // Nummeret når vi har det, ellers id-en — aldri en tom underlinje.
        subrow.append(textSpan('span', 'sb-rowprev', t.nummer || short(t.matrixId)));
        main.append(subrow);
        row.append(main);
        const apne = () => { this.q = ''; this.searchEl.value = ''; this.gnaTreff = []; this.apneDm(t.matrixId); };
        row.onclick = apne;
        row.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); apne(); } };
        this.listEl.append(row);
      }
    }
    if (!this.listEl.children.length) {
      this.listEl.append(emptyState('search', `Ingen treff på «${this.q}»`, '＋ Nytt rom', () => this.newRoom()));
    }
  }

  /**
   * Slår opp i GNÅ-katalogen mens operatøren skriver. Demper med 200 ms så et
   * søk ikke blir ett kall per tastetrykk, og krever to tegn (ett tegn treffer
   * halve registeret og er bare støy). Feil svelges: katalogen er et TILLEGG
   * til de lokale kontaktene, så en nede-hub skal ikke ødelegge søket.
   */
  private sokKatalog(q: string): void {
    const hook = this.hooks.sokGnaBrukere;
    if (!hook) return;
    if (this.katalogTimer !== null) clearTimeout(this.katalogTimer);
    const trimmet = q.trim();
    if (trimmet.length < 2) {
      if (this.gnaTreff.length) { this.gnaTreff = []; this.renderList(); }
      return;
    }
    this.katalogTimer = setTimeout(() => {
      this.katalogTimer = null;
      hook(trimmet)
        .then((treff) => {
          // Svar på et gammelt søk skal ikke overskrive et nyere.
          if (this.q.trim() !== trimmet) return;
          this.gnaTreff = treff;
          this.renderList();
        })
        .catch(() => { /* katalogen er et tillegg — lokale treff står */ });
    }, 200) as unknown as number;
  }

  private preview(c: Conv): string {
    if (c.kind === 'channel') {
      const msgs = reduceChat(this.log).messages.filter((m) => (m as ReducedMessage & { channel?: string }).channel === c.channel);
      const last = msgs[msgs.length - 1];
      return last ? `${short(last.author)}: ${last.deleted ? '(slettet)' : last.text.slice(0, 40)}` : 'Ingen meldinger ennå';
    }
    const arr = this.dm.get(c.matrixId!) ?? [];
    const last = arr[arr.length - 1];
    return last ? `${last.mine ? 'Du' : short(last.from)}: ${last.text.slice(0, 40)}` : short(c.matrixId!);
  }

  private avatar(c: Conv): HTMLElement {
    const a = el('div', 'sb-av ' + (c.kind === 'dm' ? 'person' : 'unit'));
    a.textContent = (c.name.replace(/[^A-Za-zÆØÅæøå0-9]/g, '').slice(0, 2) || '??').toUpperCase();
    // Personer får stabil, dempet identitetsfarge (scanbarhet); enheter beholder olive.
    if (c.kind === 'dm') { a.style.background = hueFor(c.matrixId ?? c.name); a.style.cursor = 'pointer'; a.title = 'Åpne kontaktkort'; a.onclick = (e) => { e.stopPropagation(); this.openCard(c); }; }
    return a;
  }

  private renderHead() {
    const c = this.active();
    this.headEl.innerHTML = '';
    const idb = el('button', 'sb-switch');
    idb.append(this.avatar(c));
    const id = el('div', 'sb-convid');
    // Kanal: vis tema hvis satt (ellers standard-sub). Person: sub som før.
    const info = c.kind === 'channel' ? this.channelInfo(c.channel!) : undefined;
    const sub = info?.topic ? info.topic : c.sub;
    id.append(textSpan('b', '', c.name), textSpan('span', '', sub));
    idb.append(id);
    // Klikk på tittel = åpne rom-info (medlemmer/tema) for kanal, kontaktkort for
    // person. Et GNÅ-rom har ingen av dem: medlemskapet ligger i Matrix, ikke i
    // hubbens ChatBoard, og vi later ikke som vi kan liste det.
    idb.onclick = () => { if (c.kind === 'channel') this.openRoster(c.channel!); else if (c.kind === 'dm') this.openCard(c); };
    idb.title = c.kind === 'channel' ? 'Rom-info: medlemmer og tema' : c.kind === 'dm' ? 'Kontaktkort' : 'GNÅ-rom (medlemskap styres i GNÅ/Matrix)';
    this.headEl.append(idb);
    const tools = el('div', 'sb-tools');
    if (c.kind === 'channel') {
      const n = info!.members.length;
      const members = btn('sb-tbtn sb-membtn', '', () => this.openRoster(c.channel!));
      members.innerHTML = MEMBERS_SVG;
      members.append(textSpan('span', 'sb-membtn-n', String(n)));
      members.title = `Medlemmer (${n}) — legg til / fjern`;
      tools.append(members);
    }
    if (c.kind === 'dm') tools.append(btn('sb-tbtn', 'ⓘ', () => this.openCard(c)));
    // Søk-i-rom (find-in-room): filtrer meldinger i aktiv samtale.
    const find = btn('sb-tbtn' + (this.findOpen ? ' active' : ''), '⌕', () => this.toggleFind());
    find.title = 'Søk i samtalen'; tools.append(find);
    // Tydelig «Eget vindu»-knapp integrert i hodet (ikke en flytende glyf oppå × ).
    if (this.hooks.onPopout && !this.hooks.embedded) {
      const pop = btn('sb-tbtn sb-popbtn', '', () => this.hooks.onPopout!());
      pop.innerHTML = POPOUT_SVG; pop.append(textSpan('span', 'sb-popbtn-lbl', 'Eget vindu'));
      pop.title = 'Åpne Feltveksler i eget vindu (egen skjerm)';
      pop.setAttribute('aria-label', 'Åpne i eget vindu');
      tools.append(pop);
    }
    this.headEl.append(tools);
    if (!this.hooks.embedded) tools.append(btn('sb-x', '×', () => this.toggle()));
    this.renderFindBar();
  }

  private toggleFind() {
    this.findOpen = !this.findOpen;
    if (!this.findOpen) this.msgQuery = '';
    this.renderHead(); this.renderMsgs();
    if (this.findOpen) { const inp = this.findEl.querySelector('input'); (inp as HTMLInputElement | null)?.focus(); }
  }
  private renderFindBar() {
    this.findEl.innerHTML = '';
    this.findEl.style.display = this.findOpen ? 'flex' : 'none';
    if (!this.findOpen) return;
    const s = searchBox('Søk i denne samtalen…', (v) => { this.msgQuery = v; this.renderMsgs(); this.updateFindCount(); });
    s.input.value = this.msgQuery;
    this.findEl.append(s.wrap);
    this.findEl.append(textSpan('span', 'sb-findcount', ''));
    this.findEl.append(btn('sb-tbtn', '×', () => this.toggleFind()));
    this.updateFindCount();
  }
  private updateFindCount() {
    const el2 = this.findEl.querySelector('.sb-findcount'); if (!el2) return;
    const n = this.msgsEl.querySelectorAll('.sb-msg').length;
    el2.textContent = this.msgQuery.trim() ? `${n} treff` : '';
  }

  /** Kjente @-kortnavn (enheter + kontakter) — for å uthev kun ekte mentions. */
  private knownMentions(): Set<string> {
    const s = new Set<string>();
    for (const c of this.convs()) if (c.kind === 'dm' && c.matrixId) s.add(short(c.matrixId).toLowerCase());
    return s;
  }
  private renderMsgs() {
    const c = this.active();
    this.msgsEl.innerHTML = '';
    const q = this.findOpen ? this.msgQuery.trim().toLowerCase() : '';
    const known = this.knownMentions();
    let unreadShown = false;
    const maybeUnread = (ts: number, mine: boolean) => {
      // «Nye meldinger»-skille: første innkommende melding etter sist-lest.
      if (!unreadShown && !mine && this.unreadBoundary > 0 && ts > this.unreadBoundary) {
        unreadShown = true;
        this.msgsEl.append(textSpan('div', 'sb-unreaddiv', 'Nye meldinger'));
      }
    };
    if (c.kind === 'channel') {
      const view = reduceChat(this.log);
      const byId = new Map(view.messages.map((m) => [m.id, m] as const));
      let msgs = view.messages.filter((m) => (m as ReducedMessage & { channel?: string }).channel === c.channel);
      if (q) msgs = msgs.filter((m) => !m.deleted && m.text.toLowerCase().includes(q));
      let prevAuthor = ''; let prevMs = 0;
      for (const m of msgs) {
        maybeUnread(m.tsMs, m.author === this.hooks.selfId);
        // Gruppér påfølgende meldinger fra samme avsender innen 5 min (rolige kolonner).
        const grouped = !q && m.author === prevAuthor && (m.tsMs - prevMs) < 5 * 60_000;
        this.msgsEl.append(this.chatRow(m, c.channel!, grouped, known, q, byId));
        prevAuthor = m.author; prevMs = m.tsMs;
      }
      if (!msgs.length) this.msgsEl.append(q ? emptyState('search', `Ingen treff på «${this.msgQuery}»`) : emptyState('chat', 'Ingen meldinger i denne kanalen ennå.', 'Skriv første melding', () => this.ta.focus()));
      this.targetEl.style.display = 'none';
    } else {
      const rom = c.kind === 'gnarom';
      let arr = this.dm.get(c.matrixId!) ?? [];
      if (q) arr = arr.filter((m) => m.text.toLowerCase().includes(q));
      let prevMine: boolean | null = null; let prevMs = 0;
      for (const m of arr) {
        maybeUnread(m.tsMs, m.mine);
        const grouped = !q && m.mine === prevMine && (m.tsMs - prevMs) < 5 * 60_000;
        this.msgsEl.append(this.dmRow(m, grouped, known, q));
        prevMine = m.mine; prevMs = m.tsMs;
      }
      if (!arr.length) {
        this.msgsEl.append(q ? emptyState('search', `Ingen treff på «${this.msgQuery}»`)
          : emptyState('chat', rom ? 'Nytt GNÅ-rom (1:flere). Innkommende romsvar vises i GNÅ.' : 'Ny 1:1 med GNÅ-bruker.', 'Skriv en melding', () => this.ta.focus()));
      }
      this.targetEl.style.display = 'flex';
      this.targetEl.innerHTML = '';
      // «som»-chipen er inngangen til identitets-lenka (privat GNÅ-bruker /
      // «swap») — den kapabiliteten satt før i GNÅ-DM-panelets hode.
      const chip = btn('sb-chip sb-idchip', short(this.matrixId), () => this.openIdentitet());
      chip.title = 'Din GNÅ-identitet — lenk privat GNÅ-bruker («swap»)';
      this.targetEl.append(textSpan('span', '', 'som'), chip, textSpan('span', 'sb-hint', rom ? '1:flere til GNÅ-rom' : '1:1 direkte til GNÅ'));
    }
    this.renderReplyBar();
    this.oppdaterVedleggKnapper();
    // Ved søk: hopp til første treff. Ellers: bunn, eller til «Nye meldinger»-skillet.
    const div0 = this.msgsEl.querySelector('.sb-unreaddiv') as HTMLElement | null;
    if (q) this.msgsEl.scrollTop = 0;
    else if (div0) div0.scrollIntoView({ block: 'center' });
    else this.msgsEl.scrollTop = this.msgsEl.scrollHeight;
  }

  /**
   * Vedlegg/tale gjelder KANAL, ikke GNÅ 1:1 — `/gna/outbound` bærer bare tekst i
   * dag. Knappene skjules derfor ikke (da ville de vært et mysterium), de
   * deaktiveres med en ærlig forklaring (grunnlov §1: ingen knapp som lyver).
   */
  private oppdaterVedleggKnapper() {
    const kanal = this.active().kind === 'channel';
    for (const b of [this.attachBtn, this.micBtn]) {
      b.disabled = !kanal;
      b.classList.toggle('off', !kanal);
    }
    // `title` er kun en hover-tooltip — usynlig på touch/nettbrett. Grunnen
    // vises derfor OGSÅ som synlig tekst i komposisjonsfeltet (checklist-note),
    // ikke bare gjemt i attributtet.
    const grunn = 'Vedlegg går bare til kanal — GNÅ 1:1/rom tar tekst (broen bærer ikke media ennå)';
    this.attachBtn.title = kanal ? 'Vedlegg (bilde/PDF) til kanalen' : grunn;
    this.micBtn.title = kanal ? 'Talemelding: klikk for opptak, klikk igjen for å sende' : grunn;
    this.vedleggNoteEl.textContent = kanal ? '' : grunn;
    this.vedleggNoteEl.style.display = kanal ? 'none' : 'block';
  }

  /** Aktiv kanal utledet av samtale-id-en. Bevisst UTEN `convs()`: skrive-
   *  indikatoren tikker hvert 2. sekund, og `convs()` bygger hele HV-katalogen
   *  på nytt hver gang den kalles. En indikator skal ikke koste et katalogoppslag. */
  private aktivKanal(): string | null {
    return this.activeId.startsWith('ch:') ? this.activeId.slice(3) : null;
  }

  /** «X skriver …» for aktiv kanal (utløper etter TYPING_TTL_MS). */
  private renderTyping() {
    const kanal = this.aktivKanal();
    if (!kanal || !this.open) { this.typingEl.textContent = ''; return; }
    const t = now();
    const who = reduceChat(this.log).typing
      .filter((x) => x.channel === kanal && x.author !== this.hooks.selfId && t - x.tsMs < TYPING_TTL_MS)
      .map((x) => short(x.author));
    this.typingEl.textContent = who.length ? `${who.join(', ')} skriver …` : '';
  }

  /** Egen skrive-indikator (strupt) — bare i kanal; GNÅ-DM har ingen slik kanal. */
  private sendTyping() {
    const kanal = this.aktivKanal();
    if (!kanal) return;
    const t = now();
    if (t - this.lastTypingSent < TYPING_STRUPE_MS) return;
    this.lastTypingSent = t;
    this.hooks.onChat({ op: 'typing', channel: kanal, author: this.hooks.selfId, tsMs: t });
  }

  private startReply(id: string) { this.replyTo = id; this.renderReplyBar(); this.ta.focus(); }
  private renderReplyBar() {
    this.replyEl.innerHTML = '';
    const kanal = this.active().kind === 'channel';
    const vis = kanal && !!this.replyTo;
    this.replyEl.style.display = vis ? 'flex' : 'none';
    if (!vis) return;
    const m = reduceChat(this.log).messages.find((x) => x.id === this.replyTo);
    this.replyEl.append(textSpan('span', 'sb-replylbl', m ? `Svarer ${short(m.author)}: ${m.text.slice(0, 60)}` : 'Svarer på melding'));
    this.replyEl.append(btn('sb-msgact', '×', () => { this.replyTo = null; this.renderReplyBar(); }));
  }

  private chatRow(m: ReducedMessage, channel: string, grouped = false, known?: Set<string>, q = '', byId?: Map<string, ReducedMessage>): HTMLElement {
    const mine = m.author === this.hooks.selfId;
    const mentionsMe = !mine && mentionsIn(m.text).includes(short(this.matrixId).toLowerCase());
    const row = el('div', 'sb-msg' + (mine ? ' me' : '') + (grouped ? ' grouped' : '') + (mentionsMe ? ' mentions-me' : ''));
    row.append(textSpan('div', 'sb-meta', `${short(m.author)} · ${hhmm(m.tsMs)}${m.editedMs ? ' · redigert' : ''}`));
    // Sitat av meldingen det svares på (replyTo) — ellers er et svar bare en
    // løsrevet linje, og det var halve poenget med svar-funksjonen.
    if (m.replyTo) {
      const kilde = byId?.get(m.replyTo);
      row.append(textSpan('div', 'sb-quote', kilde
        ? `↩ ${short(kilde.author)}: ${kilde.deleted ? '(slettet)' : kilde.text.slice(0, 80)}`
        : '↩ svar'));
    }
    const b = el('div', 'sb-bubble');
    if (m.deleted) { b.classList.add('deleted'); b.textContent = '(melding slettet)'; }
    else if (m.kind === 'image' && m.attachmentUrl) { const img = document.createElement('img'); img.className = 'sb-img'; img.src = m.attachmentUrl; img.loading = 'lazy'; b.append(img); }
    else if (m.kind === 'voice' && m.attachmentUrl) {
      const au = document.createElement('audio'); au.controls = true; au.src = m.attachmentUrl; au.preload = 'none'; b.append(au);
      if (m.durationS) b.append(textSpan('div', 'sb-dur', `${m.durationS} s tale`));
    } else if (m.kind === 'file' && m.attachmentUrl) {
      const a = document.createElement('a'); a.className = 'sb-file'; a.href = m.attachmentUrl; a.target = '_blank'; a.rel = 'noopener';
      a.textContent = 'Åpne vedlegg ↗'; b.append(a);
    } else fillText(b, m.text, known);
    row.append(b);
    void q;

    // Reaksjoner (ROGER/WILCO-kvittering) — chips per emoji med antall.
    if (!m.deleted) {
      const rx = Object.entries(m.reactions);
      if (rx.length) {
        const chips = el('div', 'sb-rx');
        for (const [emoji, authors] of rx) {
          const mineRx = authors.includes(this.hooks.selfId);
          const chip = btn('sb-rxchip' + (mineRx ? ' mine' : ''), '', () => this.react(channel, m.id, emoji, !mineRx));
          chip.append(textSpan('span', 'sb-rxe', emoji), textSpan('span', 'sb-rxn', String(authors.length)));
          chip.title = authors.map(short).join(', ');
          chips.append(chip);
        }
        row.append(chips);
      }
      // Hover-verktøy: svar + reager (alle) + rediger/slett (egne).
      const acts = el('div', 'sb-msgacts');
      const svar = btn('sb-msgact', '↩', () => this.startReply(m.id));
      svar.title = 'Svar på meldingen';
      acts.append(svar);
      const react = btn('sb-msgact', '☺', (e) => this.openReactMenu(e as unknown as MouseEvent, channel, m.id));
      react.title = 'Reager / kvittér';
      acts.append(react);
      if (mine) {
        acts.append(
          btn('sb-msgact', '✎', () => this.editMsg(channel, m)),
          btn('sb-msgact', '🗑', () => this.deleteMsg(channel, m.id)),
        );
      }
      row.append(acts);
    }
    return row;
  }

  private dmRow(m: DmMsg, grouped = false, known?: Set<string>, q = ''): HTMLElement {
    const row = el('div', 'sb-msg' + (m.mine ? ' me' : '') + (grouped ? ' grouped' : ''));
    row.append(textSpan('div', 'sb-meta', `${m.mine ? short(this.matrixId) : short(m.from)} · ${hhmm(m.tsMs)}`));
    const b = el('div', 'sb-bubble'); fillText(b, m.text, known); row.append(b);
    void q;
    if (m.mine) {
      // ⚠ «KØET» OG «LEVERT» SÅ LIKE UT FØR, og det var nettopp derfor
      // sambandsfeilen kunne stå i dagevis: en kø som ikke ble tømt var visuelt
      // identisk med et samband som virket. Nå kommer statusen fra hubben.
      const v = kvitteringVisning(
        m.failed ? 'lokal-feil' : m.pending ? 'sender' : (m.status ?? 'koet'),
        { grunn: m.grunn, koetMs: m.koetMs, forsok: m.forsok, naa: now() },
      );
      const rcpt = textSpan('div', `sb-rcpt ${v.klasse}`, v.tekst);
      rcpt.title = v.detalj;
      row.append(rcpt);
    }
    return row;
  }

  // ── Reaksjoner + rediger/slett (kontrakt + reducer finnes allerede) ─────────
  private react(channel: string, id: string, emoji: string, add: boolean) {
    const e: ChatEvent = { op: 'react', id, channel, author: this.hooks.selfId, tsMs: now(), emoji, add };
    this.log.push(e); this.hooks.onChat(e); this.renderMsgs();
  }
  private openReactMenu(ev: MouseEvent, channel: string, id: string) {
    ev.stopPropagation();
    const existing = this.el.querySelector('.sb-rxmenu'); if (existing) existing.remove();
    const menu = el('div', 'sb-rxmenu');
    for (const emoji of ACK_EMOJI) {
      const b = btn('sb-rxpick', emoji, () => { this.react(channel, id, emoji, true); menu.remove(); });
      b.title = emoji === '🅁' ? 'ROGER' : emoji === '✅' ? 'WILCO' : emoji === '📍' ? 'Sett/merk' : emoji === '❗' ? 'Viktig' : 'Mottatt';
      menu.append(b);
    }
    const t = ev.currentTarget as HTMLElement; const r = t.getBoundingClientRect();
    menu.style.left = `${Math.max(8, r.left - 40)}px`; menu.style.top = `${r.top - 44}px`;
    document.body.append(menu);
    const close = (e: MouseEvent) => { if (!menu.contains(e.target as Node)) { menu.remove(); document.removeEventListener('mousedown', close); } };
    setTimeout(() => document.addEventListener('mousedown', close), 0);
  }
  private editMsg(channel: string, m: ReducedMessage) {
    this.sporSb('Rediger melding', m.text, (raw) => {
      const text = raw?.trim();
      if (text == null || text === m.text) return;
      if (!text) { this.deleteMsg(channel, m.id); return; }
      const e: ChatEvent = { op: 'edit', id: m.id, channel, author: this.hooks.selfId, tsMs: now(), text };
      this.log.push(e); this.hooks.onChat(e); this.renderMsgs();
    });
  }
  private deleteMsg(channel: string, id: string) {
    this.bekreftSb('Slette meldingen?', 'Slett', () => {
      const e: ChatEvent = { op: 'delete', id, channel, author: this.hooks.selfId, tsMs: now() };
      this.log.push(e); this.hooks.onChat(e); this.renderMsgs();
    });
  }

  /**
   * Inline bekreftelse via sb-scrim (IKKE `window.confirm` — se
   * UX_DESIGNSYSTEM.md §4/§6b: en nettleser-dialog kan ikke stiles og
   * blokkerer hele fanen, dårlig på touch/tablet).
   */
  private bekreftSb(tekst: string, bekreftTekst: string, onBekreft: () => void): void {
    const scrim = el('div', 'sb-scrim');
    const card = el('div', 'sb-card');
    card.append(textSpan('div', 'sb-cardname', tekst));
    const close = () => scrim.remove();
    const acts = el('div', 'sb-cardacts');
    acts.append(
      btn('sb-ca primary', bekreftTekst, () => { close(); onBekreft(); }),
      btn('sb-ca', 'Avbryt', () => close()),
    );
    card.append(acts);
    scrim.append(card); this.el.append(scrim);
    scrim.onclick = (e) => { if (e.target === scrim) close(); };
  }

  /**
   * Inline fritekst via sb-scrim (IKKE `window.prompt`, samme grunn som over).
   * `onSvar` kalles nøyaktig én gang: med feltverdien ved OK/Enter, med `null`
   * ved Avbryt eller klikk utenfor — akkurat som `window.prompt` returnerte.
   */
  private sporSb(tittel: string, verdi: string, onSvar: (v: string | null) => void): void {
    const scrim = el('div', 'sb-scrim');
    const card = el('div', 'sb-card');
    card.append(textSpan('div', 'sb-cardname', tittel));
    const inp = document.createElement('input');
    inp.className = 'sb-searchinput sb-idinput';
    inp.value = verdi;
    card.append(inp);
    let svart = false;
    const close = () => { scrim.remove(); if (!svart) { svart = true; onSvar(null); } };
    const send = () => { svart = true; scrim.remove(); onSvar(inp.value); };
    const acts = el('div', 'sb-cardacts');
    acts.append(
      btn('sb-ca primary', 'OK', () => send()),
      btn('sb-ca', 'Avbryt', () => close()),
    );
    card.append(acts);
    scrim.append(card); this.el.append(scrim);
    scrim.onclick = (e) => { if (e.target === scrim) close(); };
    inp.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } };
    setTimeout(() => { inp.focus(); inp.select(); }, 0);
  }

  // ── Sending ────────────────────────────────────────────────────────────
  private send() {
    const text = this.ta.value.trim();
    if (!text) return;
    const c = this.active();
    if (c.kind === 'channel') {
      const e: ChatEvent = { op: 'post', id: rid(), channel: c.channel!, author: this.hooks.selfId, tsMs: now(), text, kind: 'text' };
      if (this.replyTo) e.replyTo = this.replyTo;
      this.replyTo = null;
      this.log.push(e); this.hooks.onChat(e); this.renderMsgs(); this.renderList();
      this.notifyMentions(c.channel!, text);
    } else {
      void this.sendUt(c.matrixId!, text);
    }
    this.ta.value = ''; this.ta.style.height = 'auto';
    this.sendBtn.disabled = true;
  }

  // ── Vedlegg + tale (flyttet inn fra Chat/TOC) ──────────────────────────────
  /** Last opp til hubben (/media, sesjons-cookie). Returnerer URL eller null. */
  private async uploadMedia(blob: Blob): Promise<string | null> {
    try {
      const r = await fetch('/media', { method: 'POST', headers: { 'content-type': blob.type }, body: blob, credentials: 'include' });
      if (!r.ok) return null;
      const j = await r.json() as { url?: string };
      return j.url ?? null;
    } catch { return null; }
  }

  private postAttachment(kind: 'voice' | 'image' | 'file', url: string, durationS?: number) {
    const c = this.active();
    if (c.kind !== 'channel') return;
    const e: ChatEvent = { op: 'post', id: rid(), channel: c.channel!, author: this.hooks.selfId, tsMs: now(), text: '', kind, attachmentUrl: url, durationS };
    if (this.replyTo) e.replyTo = this.replyTo;
    this.replyTo = null;
    this.log.push(e); this.hooks.onChat(e); this.renderMsgs(); this.renderList();
  }

  private async attachFile() {
    const f = this.fileInput.files?.[0];
    this.fileInput.value = '';
    if (!f) return;
    const url = await this.uploadMedia(f);
    if (!url) { window.alert('Opplasting feilet (krever innlogget backend).'); return; }
    this.postAttachment(f.type.startsWith('image/') ? 'image' : 'file', url);
  }

  private async toggleRecord() {
    if (this.recorder) { this.recorder.stop(); return; } // stopper pågående opptak
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      this.recStartMs = now();
      rec.ondataavailable = (ev) => { if (ev.data.size) chunks.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        this.recorder = null; this.micBtn.classList.remove('recording');
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const durationS = Math.round((now() - this.recStartMs) / 1000);
        const url = await this.uploadMedia(blob);
        if (url) this.postAttachment('voice', url, durationS);
        else window.alert('Taleopplasting feilet (krever innlogget backend).');
      };
      this.recorder = rec; this.micBtn.classList.add('recording');
      rec.start();
    } catch { window.alert('Ingen mikrofon-tilgang.'); }
  }

  // ── Identitet: hvem sender jeg som, og privat GNÅ-bruker («swap») ───────────
  /** Ekte identitet fra hubben (og evt. lenket privat GNÅ-bruker). */
  private async refreshMe() {
    try {
      const r = await fetch('/auth/me', { credentials: 'include' });
      const j = await r.json();
      const mid = j?.user?.matrixUserId;
      if (typeof mid === 'string' && mid) this.matrixId = mid;
      this.linked = typeof j?.user?.gnaPrivateUser === 'string' ? j.user.gnaPrivateUser : null;
      this.renderMsgs();
    } catch { /* offline/demo — behold utledet @skade_-id */ }
  }

  /**
   * Identitetskortet: hvem GNÅ ser som avsender, og lenka til operatørens PRIVATE
   * GNÅ-bruker («swap»). Dette var det eneste i GNÅ-DM-panelet som ikke handlet
   * om å sende en melding, og det hadde forsvunnet med panelet.
   */
  private openIdentitet() {
    const scrim = el('div', 'sb-scrim');
    const card = el('div', 'sb-card');
    scrim.append(card); this.el.append(scrim);
    const close = () => scrim.remove();
    scrim.onclick = (e) => { if (e.target === scrim) close(); };
    const status = el('div', 'sb-idstatus');
    const draw = () => {
      card.innerHTML = '';
      card.append(textSpan('div', 'sb-cardname', 'Din GNÅ-identitet'), textSpan('div', 'sb-cardunit', 'Rollen din er avsender i GNÅ'));
      const fields = el('div', 'sb-cardfields');
      fields.append(field('Sender som', this.matrixId), field('Privat GNÅ-bruker', this.linked ?? 'ikke lenket'));
      card.append(fields);
      const inp = document.createElement('input');
      inp.className = 'sb-searchinput sb-idinput';
      inp.placeholder = 'Privat GNÅ-bruker (@deg:matrix.follodefence.com)';
      inp.value = this.linked ?? '';
      card.append(inp, status);
      const acts = el('div', 'sb-cardacts');
      acts.append(
        btn('sb-ca primary', this.linked ? 'Oppdater lenke' : 'Lenk (swap)', () => void this.linkPrivat(inp.value.trim(), status, draw)),
        btn('sb-ca', 'Lukk', () => close()),
      );
      card.append(acts);
    };
    draw();
  }

  private async linkPrivat(val: string, status: HTMLElement, redraw: () => void) {
    const id = val ? normalizeMatrixId(val, this.domene(), '@') : '';
    if (id && !isMatrixUserId(id)) { status.textContent = 'Ugyldig Matrix bruker-id (@navn:domene).'; status.className = 'sb-idstatus feil'; return; }
    try {
      const r = await fetch('/gna/link', { method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'include', body: JSON.stringify({ gnaPrivateUser: id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { status.textContent = `Avvist: ${j.error ?? r.status}`; status.className = 'sb-idstatus feil'; return; }
      this.linked = j.gnaPrivateUser ?? null;
      status.textContent = this.linked ? 'Lenket — du kan «swappe» rolle ↔ person.' : 'Lenke fjernet.';
      status.className = 'sb-idstatus';
      redraw(); this.renderMsgs();
    } catch { status.textContent = 'Nettverksfeil.'; status.className = 'sb-idstatus feil'; }
  }

  /** @-mention i kanal → varsle nevnt person 1:1 (rettet tasking blir sett). */
  private notifyMentions(channel: string, text: string) {
    const wanted = new Set(mentionsIn(text));
    if (!wanted.size) return;
    for (const p of this.convs()) {
      if (p.kind !== 'dm' || !p.matrixId) continue;
      const bare = short(p.matrixId).toLowerCase();
      if (wanted.has(bare) && p.matrixId !== this.matrixId && isMatrixUserId(p.matrixId)) {
        void this.sendUt(p.matrixId, `Du ble nevnt i «${channel}»: ${text}`);
      }
    }
  }

  /** Ut til GNÅ via broen. Rom-id (`!…`) → kind='room' (1:flere), ellers 1:1. */
  private async sendUt(to: string, text: string) {
    const kind = isMatrixRoomId(to) ? 'room' : 'dm';
    const msg: DmMsg = { id: rid(), from: this.matrixId, text, tsMs: now(), mine: true, pending: true };
    const arr = this.dm.get(to) ?? []; arr.push(msg); this.dm.set(to, arr);
    this.renderMsgs(); this.renderList();
    try {
      const r = await fetch('/gna/outbound', { method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'include', body: JSON.stringify({ kind, id: msg.id, to, text, tsMs: msg.tsMs }) });
      msg.pending = false; msg.failed = !r.ok;
      // ⚠ `r.ok` BETYR BARE AT HUBBEN TOK IMOT. Leveringen bekreftes av broen,
      // og fram til bølge 209 fantes ingen vei til den sannheten — «Køet for
      // GNÅ» var alt operatøren noen gang fikk vite. Nå følger vi meldingen.
      if (r.ok) { msg.status = 'koet'; this.folgLevering(msg); }
    } catch { msg.pending = false; msg.failed = true; }
    this.renderMsgs();
  }

  /**
   * Følg leveringen av én utgående melding til statusen er endelig.
   *
   * ⚠ TRAPPER OPP (se `nestePollMs`). Uten det ville en melding som står i kø
   * fordi broen er nede spurt hubben hvert sekund i timevis — per melding.
   *
   * ⚠ GIR OPP Å SPØRRE, MEN VISER IKKE «LEVERT». Etter `MAKS_POLL` står
   * meldingen som den sist var kjent («⚠ Køet i 42 min — broen svarer ikke»),
   * aldri som noe bedre enn det vi har belegg for.
   */
  private folgLevering(msg: DmMsg): void {
    let forsok = 0;
    const spor = async (): Promise<void> => {
      forsok += 1;
      try {
        const r = await fetch(`/samband/status?id=${encodeURIComponent(msg.id)}`, {
          credentials: 'include',
        });
        // 404 = hubben kjenner ikke id-en (omstart før journalskriving). Det er
        // «vet ikke ennå», ikke en dom — vi fortsetter å spørre.
        const svar = lesStatusSvar(r.ok ? await r.json() : { status: '' });
        msg.status = svar.status;
        msg.grunn = svar.grunn;
        msg.forsok = svar.forsok;
        if (svar.koetMs) msg.koetMs = svar.koetMs;
        this.renderMsgs();
        if (erEndelig(svar.status)) return;
      } catch {
        // Nettverksfeil mot hubben endrer IKKE meldingens status: den er
        // fortsatt der den var. Vi prøver igjen.
      }
      if (forsok >= MAKS_POLL) return;
      window.setTimeout(() => void spor(), nestePollMs(forsok));
    };
    window.setTimeout(() => void spor(), nestePollMs(0));
  }

  // ── Kontaktkort ──────────────────────────────────────────────────────────
  /**
   * Kontaktkortet for en GNÅ-bruker.
   *
   * ⚠ TRE TING VAR GALT (eierrapport 2026-08-18: «Dette er svakt»):
   *  1. Toppen viste den rå id-en («gna4796000526») som om den var et navn,
   *     fordi `convs()` aldri slo opp navnet. Nå står NAVNET øverst når
   *     katalogen kjenner det — og når den ikke gjør det, sier kortet det rett
   *     ut framfor å presentere et telefonnummer som en identitet.
   *  2. Nummeret manglet helt, selv om katalogen har det.
   *  3. Det fantes ingen vei til kartet. «Finn ham» er det operatøren faktisk
   *     vil gjøre fra et kontaktkort, og den knappen skriver ut ALDEREN på
   *     posisjonen — et spor på ni minutter er «her VAR han», ikke «her er han».
   */
  private openCard(c: Conv) {
    const mid = c.matrixId!;
    const kjent = this.navnCache.get(mid);
    const harNavn = !!kjent?.navn;
    const tittel = harNavn ? kjent!.navn : short(mid);
    const scrim = el('div', 'sb-scrim');
    const card = el('div', 'sb-card');
    const top = el('div', 'sb-cardtop');
    const av = el('div', 'sb-av person sb-cardav');
    av.textContent = (tittel.slice(0, 2) || '??').toUpperCase();
    top.append(
      av,
      textSpan('div', 'sb-cardname', tittel),
      textSpan(
        'div', 'sb-cardunit',
        harNavn ? (kjent!.nummer || 'GNÅ-bruker') : 'Navn ikke registrert i GNÅ-katalogen',
      ),
    );
    const fields = el('div', 'sb-cardfields');
    if (harNavn && kjent!.nummer) fields.append(field('Telefon', kjent!.nummer));
    fields.append(field('GNÅ-id', mid), field('Type', 'GNÅ-bruker / enhet'));

    // Siste kjente posisjon — leses NÅ, slik at alderen er alderen ved åpning.
    const pos = this.hooks.onFinnIKart ? this.hooks.onFinnIKart(mid) : null;
    if (pos) {
      fields.append(field('Sist sett', `${alderTekstKort(now() - pos.tsMs)} siden`));
    } else if (this.hooks.onFinnIKart) {
      fields.append(field('Sist sett', 'Ingen posisjon mottatt'));
    }

    const acts = el('div', 'sb-cardacts');
    acts.append(
      btn('sb-ca primary', '💬 Melding', () => {
        this.activeId = `dm:${mid}`; close(); this.renderAll(); this.ta.focus();
      }),
    );
    if (this.hooks.onFinnIKart) {
      // ⚠ KNAPPEN LYVER IKKE NÅR DET IKKE FINNES NOE Å FLY TIL. Den er
      // deaktivert med en forklaring i teksten, i stedet for å være trykkbar og
      // gjøre ingenting — eller å panorere til et punkt vi ikke har.
      const finn = btn('sb-ca', pos ? '🗺 Finn i kartet' : '🗺 Ingen posisjon', () => {
        const p = this.hooks.onFinnIKart?.(mid);
        if (!p) return;
        close();
      });
      finn.disabled = !pos;
      if (!pos) finn.title = 'Personen har ikke rapportert posisjon til SKADE ennå.';
      acts.append(finn);
    }
    acts.append(
      btn('sb-ca', '↗ Del kontaktkort', () => this.openShare(mid, tittel)),
      btn('sb-ca wide', 'Lukk', () => close()),
    );
    card.append(top, fields, acts); scrim.append(card); this.el.append(scrim);
    const close = () => scrim.remove();
    scrim.onclick = (e) => { if (e.target === scrim) close(); };
    // Mangler navnet, prøv ett oppslag mens kortet står åpent — da får
    // operatøren navnet uten å måtte lukke og åpne igjen.
    if (!harNavn) void this.loesNavn([mid]);
  }

  private openShare(mid: string, name: string) {
    const scrim = el('div', 'sb-scrim');
    const card = el('div', 'sb-card');
    card.append(textSpan('div', 'sb-cardname', 'Del kontaktkort'), textSpan('div', 'sb-cardunit', `${name} → velg mottaker`));
    const search = searchBox('Søk samtale eller person…', (v) => draw(v));
    const list = el('div', 'sb-cardfields');
    card.append(search.wrap, list); scrim.append(card); this.el.append(scrim);
    const close = () => scrim.remove();
    scrim.onclick = (e) => { if (e.target === scrim) close(); };
    const draw = (q: string) => {
      const ql = q.trim().toLowerCase();
      const targets = this.convs().filter((c) => c.id !== `dm:${mid}`).filter((c) => !ql || (c.name + c.sub).toLowerCase().includes(ql));
      list.innerHTML = '';
      for (const t of targets.slice(0, 30)) {
        const r = el('div', 'sb-row'); r.append(this.avatar(t)); r.append(textSpan('div', 'sb-rowname', t.name));
        r.onclick = () => { this.doShare(mid, name, t); close(); };
        list.append(r);
      }
      if (!list.children.length) list.append(textSpan('div', 'sb-empty', 'Ingen treff'));
    };
    draw('');
    setTimeout(() => search.input.focus(), 30);
  }

  private doShare(mid: string, name: string, target: Conv) {
    const line = `📇 Kontaktkort: ${name} — ${mid}`;
    if (target.kind === 'channel') {
      const e: ChatEvent = { op: 'post', id: rid(), channel: target.channel!, author: this.hooks.selfId, tsMs: now(), text: line, kind: 'text' };
      this.log.push(e); this.hooks.onChat(e);
    } else {
      void this.sendUt(target.matrixId!, line);
    }
    this.activeId = target.id; this.renderAll();
  }

  // ── Rom-info: medlemmer + tema (kanalmedlemskap) ────────────────────────────
  /** Rom-info-skuff: hvem er på nettet, sett tema, legg til / fjern medlem. */
  private openRoster(channel: string) {
    const scrim = el('div', 'sb-scrim');
    const card = el('div', 'sb-card sb-roster');
    scrim.append(card); this.el.append(scrim);
    const close = () => scrim.remove();
    scrim.onclick = (e) => { if (e.target === scrim) close(); };
    const draw = () => {
      const info = this.channelInfo(channel);
      card.innerHTML = '';
      // Hode
      const top = el('div', 'sb-cardtop');
      const av = el('div', 'sb-av unit sb-cardav'); av.textContent = (channel.replace(/[^A-Za-zÆØÅæøå0-9]/g, '').slice(0, 2) || '#').toUpperCase();
      top.append(av, textSpan('div', 'sb-cardname', channel), textSpan('div', 'sb-cardunit', `Kanal · ${info.members.length} medlem${info.members.length === 1 ? '' : 'mer'}`));
      card.append(top);
      // Tema (redigerbart)
      const topicWrap = el('div', 'sb-topic');
      topicWrap.append(textSpan('div', 'sb-topic-lbl', 'Tema / formål'));
      const topicVal = textSpan('div', 'sb-topic-val', info.topic || 'Ikke satt — klikk for å sette nettets formål.');
      if (!info.topic) topicVal.classList.add('empty');
      topicVal.onclick = () => this.setTopic(channel, info.topic ?? '', draw);
      topicWrap.append(topicVal);
      card.append(topicWrap);
      // Medlemsliste
      const lbl = el('div', 'sb-roster-hd');
      lbl.append(textSpan('span', '', 'Medlemmer'));
      lbl.append(btn('sb-ca primary sb-addmem', '＋ Legg til', () => this.pickMember(channel, draw)));
      card.append(lbl);
      const list = el('div', 'sb-cardfields');
      if (!info.members.length) {
        list.append(emptyState('members', 'Ingen medlemmer ennå.', '＋ Legg til noen', () => this.pickMember(channel, draw)));
      }
      for (const m of info.members) {
        const r = el('div', 'sb-row sb-memrow');
        const a = el('div', 'sb-av person'); a.textContent = ((m.name ?? m.id).replace(/[^A-Za-zÆØÅæøå0-9]/g, '').slice(0, 2) || '??').toUpperCase();
        r.append(a);
        const mm = el('div', 'sb-rowmain');
        mm.append(textSpan('div', 'sb-rowname', m.name ?? short(m.id)));
        mm.append(textSpan('div', 'sb-rowprev', short(m.id)));
        r.append(mm);
        const rm = btn('sb-tbtn sb-memrm', '×', () => this.removeMember(channel, m.id, m.name ?? short(m.id), draw));
        rm.title = 'Fjern fra kanalen';
        r.append(rm);
        list.append(r);
      }
      card.append(list);
      // Bunn: bli med selv / forlat
      const acts = el('div', 'sb-cardacts');
      if (this.amMember(channel)) acts.append(btn('sb-ca', 'Forlat kanalen', () => { this.removeMember(channel, this.hooks.selfId, short(this.matrixId), draw); }));
      else acts.append(btn('sb-ca', 'Bli med selv', () => { this.addMemberRef(channel, this.hooks.selfId, short(this.matrixId), false); draw(); }));
      acts.append(btn('sb-ca wide', 'Lukk', () => close()));
      card.append(acts);
    };
    draw();
  }

  /** Personvelger: legg til enhet/kontakt i kanalen. */
  private pickMember(channel: string, redraw: () => void) {
    const info = this.channelInfo(channel);
    const have = new Set(info.members.map((m) => m.id));
    const scrim = el('div', 'sb-scrim');
    const card = el('div', 'sb-card');
    card.append(textSpan('div', 'sb-cardname', 'Legg til i kanalen'), textSpan('div', 'sb-cardunit', `${channel} → velg person/enhet`));
    const search = searchBox('Søk person, lag, tropp…', (v) => draw(v));
    const list = el('div', 'sb-cardfields');
    card.append(search.wrap, list); scrim.append(card); this.el.append(scrim);
    const close = () => scrim.remove();
    scrim.onclick = (e) => { if (e.target === scrim) close(); };
    const draw = (q: string) => {
      const ql = q.trim().toLowerCase();
      const people = this.convs().filter((c) => c.kind === 'dm' && !have.has(c.matrixId!))
        .filter((c) => !ql || (c.name + ' ' + c.sub).toLowerCase().includes(ql));
      list.innerHTML = '';
      for (const t of people.slice(0, 40)) {
        const r = el('div', 'sb-row'); r.append(this.avatar(t));
        const mm = el('div', 'sb-rowmain');
        mm.append(textSpan('div', 'sb-rowname', t.name), textSpan('div', 'sb-rowprev', t.sub));
        r.append(mm);
        r.onclick = () => { this.addMemberRef(channel, t.matrixId!, t.name, true); close(); redraw(); };
        list.append(r);
      }
      if (!list.children.length) list.append(emptyState('members', ql ? 'Ingen treff' : 'Alle er allerede med.'));
    };
    draw('');
    setTimeout(() => search.input.focus(), 30);
  }

  /** Emit «member add» + valgfri GNÅ-varsling så personen faktisk blir pinget. */
  private addMemberRef(channel: string, subject: string, name: string, notify: boolean) {
    const e: ChatEvent = { op: 'member', channel, author: this.hooks.selfId, tsMs: now(), subject, action: 'add', name };
    this.log.push(e); this.hooks.onChat(e);
    // Systemlinje i kanalen (revisjonsspor + synlig for alle).
    const sys: ChatEvent = { op: 'post', id: rid(), channel, author: this.hooks.selfId, tsMs: now(), text: `${name} lagt til på nettet.`, kind: 'text' };
    this.log.push(sys); this.hooks.onChat(sys);
    // Reell varsling: si fra til GNÅ-brukeren 1:1 at de er satt på nettet.
    if (notify && isMatrixUserId(subject)) void this.sendUt(subject, `Du er satt på kanalen «${channel}». Følg med her.`);
    this.renderHead(); this.renderList();
  }

  private removeMember(channel: string, subject: string, name: string, redraw: () => void) {
    this.bekreftSb(`Fjerne ${name} fra «${channel}»?`, 'Fjern', () => {
      const e: ChatEvent = { op: 'member', channel, author: this.hooks.selfId, tsMs: now(), subject, action: 'remove', name };
      this.log.push(e); this.hooks.onChat(e);
      const sys: ChatEvent = { op: 'post', id: rid(), channel, author: this.hooks.selfId, tsMs: now(), text: `${name} fjernet fra nettet.`, kind: 'text' };
      this.log.push(sys); this.hooks.onChat(sys);
      this.renderHead(); this.renderList(); redraw();
    });
  }

  private setTopic(channel: string, current: string, redraw: () => void) {
    this.sporSb('Tema / formål for kanalen', current, (raw) => {
      const text = raw?.trim();
      if (text == null) return;
      const e: ChatEvent = { op: 'topic', channel, author: this.hooks.selfId, tsMs: now(), text };
      this.log.push(e); this.hooks.onChat(e);
      this.renderHead(); redraw();
    });
  }

  private newRoom() {
    this.sporSb('Navn på nytt rom/kanal', '', (raw) => {
      const name = raw?.trim();
      if (!name) return;
      this.newRoomOpprett(name);
    });
  }

  private newRoomOpprett(name: string) {
    // Kanaler er implisitte (opprettes ved første melding). Bytt til den + fokus.
    this.activeId = `ch:${name}`;
    if (!this.convs().find((c) => c.id === `ch:${name}`)) {
      // legg en systemmelding så kanalen dukker opp i lista
      const e: ChatEvent = { op: 'post', id: rid(), channel: name, author: this.hooks.selfId, tsMs: now(), text: `Kanal «${name}» opprettet.`, kind: 'text' };
      this.log.push(e); this.hooks.onChat(e);
    }
    // Oppretter blir automatisk medlem — så roster ikke starter tomt.
    if (!this.amMember(name)) this.addMemberRef(name, this.hooks.selfId, short(this.matrixId), false);
    this.markRead(`ch:${name}`);
    this.renderAll(); this.ta.focus();
    // Åpne rom-info med én gang så man kan legge til flere.
    this.openRoster(name);
  }
}

// ── DOM-hjelpere ────────────────────────────────────────────────────────────
function el(tag: string, cls: string): HTMLElement { const e = document.createElement(tag); e.className = cls; return e; }
function textSpan(tag: string, cls: string, text: string): HTMLElement { const e = document.createElement(tag); if (cls) e.className = cls; e.textContent = text; return e; }
function btn(cls: string, label: string, onclick: (e: MouseEvent) => void): HTMLButtonElement { const b = document.createElement('button'); b.className = cls; b.textContent = label; b.onclick = onclick; return b; }
function field(label: string, value: string): HTMLElement { const f = el('div', 'sb-cf'); f.append(textSpan('dt', '', label), textSpan('dd', '', value)); return f; }
// @-mention: del teksten i noder (XSS-trygt — hver del via textContent) og uthev
// @token. `known` = kjente kortnavn (uthever kun ekte enheter/kontakter).
const MENTION_RE = /(@[\wÆØÅæøå.\-/]+)/g;
function fillText(container: HTMLElement, text: string, known?: Set<string>): void {
  container.textContent = '';
  for (const part of text.split(MENTION_RE)) {
    if (!part) continue;
    if (part[0] === '@') {
      const bare = part.slice(1).toLowerCase();
      const hit = !known || known.has(bare);
      container.append(textSpan('span', hit ? 'sb-mention' : '', part));
    } else container.append(document.createTextNode(part));
  }
}
/** Trekk ut @-mentions fra en tekst (kortnavn, små bokstaver). */
function mentionsIn(text: string): string[] {
  return (text.match(MENTION_RE) ?? []).map((m) => m.slice(1).toLowerCase());
}
/** Stabil, dempet militær-tone (HSL) per identitet — gir avatarer gjenkjennelig
   farge (Element-scanbarhet) uten å bryte paletten. */
function hueFor(id: string): string {
  let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return `hsl(${h} 22% 30%)`;
}
/** Rik tomtilstand: dempet linje-ikon + veiledende tekst + valgfri inline-CTA. */
function emptyState(kind: keyof typeof EMPTY_SVG, text: string, ctaLabel?: string, ctaFn?: () => void): HTMLElement {
  const wrap = el('div', 'sb-empty');
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.6'); svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
  svg.innerHTML = EMPTY_SVG[kind] ?? EMPTY_SVG.chat;
  wrap.append(svg, textSpan('div', 'sb-empty-txt', text));
  if (ctaLabel && ctaFn) wrap.append(btn('sb-empty-cta', ctaLabel, ctaFn));
  return wrap;
}
function searchBox(placeholder: string, oninput: (v: string) => void): { wrap: HTMLElement; input: HTMLInputElement } {
  const wrap = el('div', 'sb-search');
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('stroke-width', '2'); svg.setAttribute('class', 'sb-searchicon');
  svg.innerHTML = '<circle cx="11" cy="11" r="7" fill="none" stroke="currentColor"/><path d="M21 21l-4.3-4.3" fill="none" stroke="currentColor"/>';
  const input = document.createElement('input'); input.className = 'sb-searchinput'; input.placeholder = placeholder; input.autocomplete = 'off';
  input.oninput = () => oninput(input.value);
  wrap.append(svg, input);
  return { wrap, input };
}
